import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { InfoTooltip } from "@/components/info-tooltip";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  Package,
  ShoppingCart,
  TrendingDown,
  Search,
  Download,
  RefreshCw,
  CheckCircle2,
  Clock,
  Info,
  Brain,
} from "lucide-react";
import { toast } from "sonner";
import { mockStockItems } from "@/data/mockStockItems";

// Calculate reorder needs based on current stock levels
const calculateReorderNeeds = () => {
  return mockStockItems
    .filter(item => item.reorderPoint && item.reorderPoint > 0)
    .map(item => {
      const currentStock = item.currentStock || 0;
      const reorderPoint = item.reorderPoint || 0;
      const reorderQuantity = item.reorderQuantity || 0;
      const safetyStock = item.safetyStock || 0;
      const leadTimeDays = item.leadTimeDays || 7;
      
      // Calculate days of stock remaining (rough estimate)
      const avgDailyUsage = reorderPoint / leadTimeDays;
      const daysRemaining = avgDailyUsage > 0 ? Math.floor(currentStock / avgDailyUsage) : 999;
      
      // Determine urgency
      let urgency: "critical" | "high" | "medium" | "low" = "low";
      if (currentStock <= safetyStock) urgency = "critical";
      else if (currentStock <= reorderPoint) urgency = "high";
      else if (currentStock <= reorderPoint * 1.2) urgency = "medium";
      
      // Calculate suggested order quantity
      const stockDeficit = Math.max(0, reorderPoint - currentStock);
      const suggestedOrderQty = reorderQuantity > 0 ? reorderQuantity : Math.max(stockDeficit, reorderPoint);
      
      return {
        ...item,
        daysRemaining,
        urgency,
        stockDeficit,
        suggestedOrderQty,
        needsReorder: currentStock <= reorderPoint,
      };
    })
    .filter(item => item.needsReorder)
    .sort((a, b) => {
      // Sort by urgency first, then by days remaining
      const urgencyOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      if (urgencyOrder[a.urgency] !== urgencyOrder[b.urgency]) {
        return urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
      }
      return a.daysRemaining - b.daysRemaining;
    });
};

export default function ReorderRecommendationsPage() {
  const { isHelpOpen, toggleHelp } = useContextualHelp();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [urgencyFilter, setUrgencyFilter] = useState("all");
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  const reorderItems = useMemo(() => calculateReorderNeeds(), []);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set(reorderItems.map(item => item.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [reorderItems]);

  // Filter items
  const filteredItems = useMemo(() => {
    return reorderItems.filter(item => {
      const matchesSearch = !searchQuery || 
        item.stockCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
      
      const matchesUrgency = urgencyFilter === "all" || item.urgency === urgencyFilter;
      
      return matchesSearch && matchesCategory && matchesUrgency;
    });
  }, [reorderItems, searchQuery, categoryFilter, urgencyFilter]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const critical = reorderItems.filter(i => i.urgency === "critical").length;
    const high = reorderItems.filter(i => i.urgency === "high").length;
    const totalValue = reorderItems.reduce((sum, item) => sum + (item.suggestedOrderQty * (item.unitCost || 0)), 0);
    
    return {
      totalItems: reorderItems.length,
      critical,
      high,
      totalValue,
    };
  }, [reorderItems]);

  const getUrgencyBadge = (urgency: string) => {
    switch (urgency) {
      case "critical":
        return <Badge variant="destructive">Critical</Badge>;
      case "high":
        return <Badge variant="warning">High</Badge>;
      case "medium":
        return <Badge variant="info">Medium</Badge>;
      default:
        return <Badge variant="outline">Low</Badge>;
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(item => item.stockCode)));
    }
  };

  const handleSelectItem = (stockCode: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(stockCode)) {
      newSelected.delete(stockCode);
    } else {
      newSelected.add(stockCode);
    }
    setSelectedItems(newSelected);
  };

  const handleCreatePurchaseOrders = () => {
    if (selectedItems.size === 0) {
      toast.error("Please select at least one item");
      return;
    }
    toast.success(`Creating purchase orders for ${selectedItems.size} item(s)`);
    setSelectedItems(new Set());
  };

  const handleExport = () => {
    toast.success("Exporting reorder recommendations to CSV");
  };

  const helpContent = {
    title: "Reorder Recommendations",
    sections: [
      {
        title: "Overview",
        content: "This page shows items that currently need reordering based on their actual stock levels versus reorder points. It focuses on immediate replenishment needs.",
      },
      {
        title: "Urgency Levels",
        items: [
          "Critical: Stock is at or below safety stock level",
          "High: Stock is at or below reorder point",
          "Medium: Stock is within 20% above reorder point",
          "Low: Stock is above reorder point but flagged for monitoring",
        ],
      },
      {
        title: "How It Works",
        items: [
          "System checks current stock against reorder points",
          "Calculates days of stock remaining based on usage",
          "Suggests order quantities based on reorder quantity settings",
          "Prioritizes items by urgency and days remaining",
        ],
      },
      {
        title: "Actions",
        items: [
          "Select items and create purchase orders in bulk",
          "Export recommendations for review",
          "View item details for more information",
        ],
      },
    ],
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Reorder Recommendations</h1>
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
              <Clock className="h-3 w-3 mr-1" />
              Current Needs
            </Badge>
          </div>
          <p className="text-muted-foreground mt-2">
            Items that need immediate reordering based on current stock levels
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <HelpButton onClick={toggleHelp} />
        </div>
      </div>

      {/* Info about AI Forecasting */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
        <InfoTooltip content={
          <div className="space-y-1">
            <p className="font-semibold">Looking ahead?</p>
            <p className="text-xs">For AI-predicted future reorder needs (30-90 days), check the <strong>"Top Items at Risk"</strong> section in the <strong>Overview</strong> tab of the <strong>AI Forecasting</strong> page.</p>
          </div>
        } />
        <span>Hover for AI forecasting info</span>
      </div>

      {/* Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StaticMetricCardAlt
          title="Items Needing Reorder"
          value={metrics.totalItems.toString()}
          icon={Package}
          change={`${metrics.totalItems}`}
          changeLabel="items below reorder point"
          variant="error"
        />
        <StaticMetricCardAlt
          title="Critical Items"
          value={metrics.critical.toString()}
          icon={AlertTriangle}
          change={`${metrics.critical}`}
          changeLabel="at or below safety stock"
          variant="error"
        />
        <StaticMetricCardAlt
          title="High Priority"
          value={metrics.high.toString()}
          icon={TrendingDown}
          change={`${metrics.high}`}
          changeLabel="at reorder point"
          variant="warning"
        />
        <StaticMetricCardAlt
          title="Est. Order Value"
          value={`${metrics.totalValue.toLocaleString()}`}
          icon={ShoppingCart}
          change={`${metrics.totalValue.toLocaleString()}`}
          changeLabel="total suggested orders"
          variant="info"
        />
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Current Reorder Needs</CardTitle>
              <CardDescription>
                Items currently at or below their reorder points
              </CardDescription>
            </div>
            <Button
              onClick={handleCreatePurchaseOrders}
              disabled={selectedItems.size === 0}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Create POs ({selectedItems.size})
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2 flex-1">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label>Category:</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label>Urgency:</Label>
              <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator className="my-4" />

          {/* Results count */}
          <div className="text-sm text-muted-foreground mb-4">
            Showing {filteredItems.length} of {reorderItems.length} items
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4"
                    />
                  </TableHead>
                  <TableHead>Stock Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Current Stock</TableHead>
                  <TableHead className="text-right">Reorder Point</TableHead>
                  <TableHead className="text-right">Safety Stock</TableHead>
                  <TableHead className="text-right">Suggested Qty</TableHead>
                  <TableHead className="text-right">Days Left</TableHead>
                  <TableHead>Urgency</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                      <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-600" />
                      <p className="font-medium">All items are well-stocked!</p>
                      <p className="text-sm mt-1">No items currently need reordering.</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map(item => (
                    <TableRow key={item.stockCode}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.stockCode)}
                          onChange={() => handleSelectItem(item.stockCode)}
                          className="h-4 w-4"
                        />
                      </TableCell>
                      <TableCell className="font-medium">{item.stockCode}</TableCell>
                      <TableCell>
                        <div className="max-w-xs truncate">{item.description}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.category}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{item.currentStock}</TableCell>
                      <TableCell className="text-right">{item.reorderPoint}</TableCell>
                      <TableCell className="text-right">{item.safetyStock || "-"}</TableCell>
                      <TableCell className="text-right font-medium text-blue-600">
                        {item.suggestedOrderQty}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={item.daysRemaining <= 7 ? "text-red-600 font-medium" : ""}>
                          {item.daysRemaining > 100 ? "N/A" : `${item.daysRemaining} days`}
                        </span>
                      </TableCell>
                      <TableCell>
                        {getUrgencyBadge(item.urgency)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={toggleHelp}
        content={helpContent}
      />
    </div>
  );
}
